import React, { useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import * as yup from "yup";
import FormField from "../../molecules/FormField";
import Button from "../../atoms/Button";
import Input from "../../atoms/Input";
import Separator from "../../atoms/Separator";
import styled from "styled-components";
import { FontSizes } from "../../../theme/fonts";
import Modal from "../../molecules/Modal";
import { v4 as uuidv4 } from "uuid";
import {
  deleteCheckoutPage,
  getCheckoutPageById,
  updateCheckoutPage,
} from "../../../apis/Admin";
import ModalInfo from "../../molecules/ModalInfo";
import { GlobalInformation } from "../../../global/information";

interface ConfigCheckoutProps {
  isOpen: boolean;
  onRequestClose: () => void;
  checkoutId?: string;
}

const ConfigCheckoutPage = ({
  isOpen,
  onRequestClose,
  checkoutId,
}: ConfigCheckoutProps) => {
  const [modalConfirmationDelete, setModalConfirmationDelete] = useState<{
    isDeleting: boolean;
    show: boolean;
  }>({ isDeleting: false, show: false });

  const [modalSuccess, setModalSuccess] = useState<{
    show: boolean;
  }>({ show: false });

  const [id, setId] = useState<string | undefined>(undefined);
  const [initialValues, setInitialValues] = useState<
    | {
        videoPromotionURL: string;
        priceTotal: number;
        discountPercentage: number;
        updatedAt?: number;
      }
    | undefined
  >(undefined);

  const isEditing = checkoutId !== undefined;

  useEffect(() => {
    if (checkoutId === undefined) {
      const randomId = uuidv4();
      setId(randomId);
    } else setId(checkoutId);
  }, [checkoutId]);

  useEffect(() => {
    if (id !== undefined) {
      if (isEditing) {
        const unsubscribe = getCheckoutPageById(id, (data) => {
          setInitialValues(data);
        });
        return () => unsubscribe();
      } else {
        setInitialValues(undefined);
      }
    }
  }, [id, isEditing]);

  const handleOnSubmit = async (data: {
    videoPromotionURL: string;
    priceTotal: number;
    discountPercentage: number;
  }) => {
    if (id !== undefined) {
      await updateCheckoutPage(id, {
        videoPromotionURL: data.videoPromotionURL,
        priceTotal: data.priceTotal,
        discountPercentage: data.discountPercentage,
      });
      setModalSuccess({ show: true });
    }
  };

  const handleOnDelete = async () => {
    if (id !== undefined && isEditing) {
      setModalConfirmationDelete({ isDeleting: true, show: true });
      await deleteCheckoutPage(id);
      setModalConfirmationDelete({ isDeleting: false, show: false });
      onRequestClose();
    }
  };

  return (
    <>
      <Modal
        title="Pasarela de pago"
        isOpen={isOpen}
        onRequestClose={onRequestClose}
      >
        <Formik
          enableReinitialize={true}
          initialValues={{
            videoPromotionURL: (
              initialValues?.videoPromotionURL || ""
            ).toString(),
            priceTotal: (initialValues?.priceTotal || "").toString(),
            discountPercentage: (
              initialValues?.discountPercentage || ""
            ).toString(),
          }}
          validationSchema={yup.object({
            videoPromotionURL: yup
              .string()
              .url("No has ingresado un URL correcto.")
              .required("Es necesario este dato."),
            priceTotal: yup
              .number()
              .typeError("Debe ser un número.")
              .min(0, "Debe ser mayor a 0.")
              .required("Es necesario este dato."),
            discountPercentage: yup
              .number()
              .typeError("Debe ser un número.")
              .min(0, "Debe ser mayor o igual a 0.")
              .max(100, "Debe ser menor o igual a 100.")
              .required("Es necesario este dato."),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            await handleOnSubmit({
              videoPromotionURL: values.videoPromotionURL,
              priceTotal: parseFloat(values.priceTotal),
              discountPercentage: parseFloat(values.discountPercentage),
            });
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            setFieldTouched,
            submitForm,
            isSubmitting,
          }) => (
            <>
              <FormField
                show={
                  errors.videoPromotionURL !== undefined &&
                  touched.videoPromotionURL === true
                }
                type="error"
                message={errors.videoPromotionURL}
              >
                <Label>Video Promocional (URL)</Label>
                <Separator type="small" />
                <Input
                  hasError={
                    errors.videoPromotionURL !== undefined &&
                    touched.videoPromotionURL === true
                  }
                  placeholder="https://www.youtube.com/watch?v="
                  value={values.videoPromotionURL}
                  onChangeValue={handleChange("videoPromotionURL")}
                  onBlur={handleBlur("videoPromotionURL")}
                />
              </FormField>
              <Separator type="medium" />
              <FormField
                show={
                  errors.priceTotal !== undefined && touched.priceTotal === true
                }
                type="error"
                message={errors.priceTotal}
              >
                <Label>Precio ($)</Label>
                <Separator type="small" />
                <Input
                  hasError={
                    errors.priceTotal !== undefined &&
                    touched.priceTotal === true
                  }
                  placeholder="40.00"
                  value={values.priceTotal}
                  onChangeValue={handleChange("priceTotal")}
                  onBlur={handleBlur("priceTotal")}
                />
              </FormField>
              <Separator type="medium" />
              <FormField
                show={
                  errors.discountPercentage !== undefined &&
                  touched.discountPercentage === true
                }
                type="error"
                message={errors.discountPercentage}
              >
                <Label>Descuento (%)</Label>
                <Separator type="small" />
                <Input
                  hasError={
                    errors.discountPercentage !== undefined &&
                    touched.discountPercentage === true
                  }
                  placeholder="50.00"
                  value={values.discountPercentage}
                  onChangeValue={handleChange("discountPercentage")}
                  onBlur={handleBlur("discountPercentage")}
                />
              </FormField>

              <Separator type="xlarge" />
              <ButtonsWrapper>
                <Button
                  type="primary-inverted"
                  label="Cancelar"
                  onClick={onRequestClose}
                />
                {isEditing && (
                  <Button
                    type="error-inverted"
                    label="Eliminar"
                    onClick={() =>
                      setModalConfirmationDelete({
                        isDeleting: false,
                        show: true,
                      })
                    }
                  />
                )}
                <Button
                  type="primary"
                  label={isEditing ? "Guardar" : "Añadir"}
                  loading={isSubmitting}
                  onClick={() => {
                    submitForm();
                  }}
                />
              </ButtonsWrapper>
            </>
          )}
        </Formik>
      </Modal>
      <ModalInfo
        isOpen={modalSuccess.show}
        onRequestClose={() => setModalSuccess({ show: false })}
        type="check"
        message={
          <Message>
            ¡Se {isEditing ? "guardó" : "añadió"} exitosamente la pasarela de
            pago!
            <br />
            <br />
            La página es:
            <a
              href={`${GlobalInformation.hostingURL}/aleman-para-turistas/${id}`}
            >
              {GlobalInformation.hostingURL}/aleman-para-turistas/{id}
            </a>
          </Message>
        }
        actions={
          <Button
            type="primary"
            label="Aceptar"
            onClick={() => setModalSuccess({ show: false })}
          />
        }
      />
      <ModalInfo
        isOpen={modalConfirmationDelete.show}
        onRequestClose={() =>
          setModalConfirmationDelete({ isDeleting: false, show: false })
        }
        type="question"
        message={
          <Message>
            ¿Estás seguro de eliminar esta Pasarela de pago?
            <br />
            <br />
            ¡No podrás recuperarla!
          </Message>
        }
        actions={
          <>
            <Button
              type="primary-inverted"
              label="Cancelar"
              onClick={() =>
                setModalConfirmationDelete({ isDeleting: false, show: false })
              }
            />
            <Button
              loading={modalConfirmationDelete.isDeleting}
              type="primary"
              label="Sí, borrar"
              onClick={handleOnDelete}
            />
          </>
        }
      />
    </>
  );
};

const Label = styled.div`
  font-size: ${FontSizes.Paragraph};
  font-weight: bold;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  & > *:not(:first-child) {
    margin-left: ${4 * 2}px;
  }

  & > :last-child {
    flex: 2;
  }
`;

const Message = styled.div`
  text-align: center;
`;

export default ConfigCheckoutPage;
